import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, X, Loader2 } from 'lucide-react';

const RevenueRangesDialog = ({
  open,
  onOpenChange,
  revenueTargetType,
  setRevenueTargetType,
  revenueCurrencyFallback,
  setRevenueCurrencyFallback,
  selectedRevenueRanges,
  setSelectedRevenueRanges,
  revenueSuggestions,
  customRangeInput,
  setCustomRangeInput,
  onSave,
}) => {
  // Select target type and set default ranges
  const selectRevenueTargetType = (type) => {
    setRevenueTargetType(type);
    // Pre-select common ranges based on target type
    if (type === 'smb') {
      setSelectedRevenueRanges(['$0 - $1M', '$1M - $10M', '$10M - $50M']);
    } else if (type === 'enterprise') {
      setSelectedRevenueRanges(['$50M - $100M', '$100M - $500M', '$500M - $1B', '$1B+']);
    }
  };

  const addSuggestedRange = (range) => {
    if (!selectedRevenueRanges.includes(range)) {
      setSelectedRevenueRanges([...selectedRevenueRanges, range]);
    }
  };

  const addCustomRange = () => {
    if (customRangeInput.trim() && !selectedRevenueRanges.includes(customRangeInput.trim())) {
      setSelectedRevenueRanges([...selectedRevenueRanges, customRangeInput.trim()]);
      setCustomRangeInput('');
    }
  };

  const removeRange = (rangeToRemove) => {
    setSelectedRevenueRanges(selectedRevenueRanges.filter(r => r !== rangeToRemove));
  };

  const isValid = revenueTargetType && selectedRevenueRanges.length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Configure Revenue Ranges</DialogTitle>
          <DialogDescription>
            Select your target customer type and choose at least 2 ranges
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4 overflow-y-auto flex-1 pr-2">
          {/* Target Type Selection - Required */}
          <div className={`p-4 rounded-lg ${revenueTargetType ? 'bg-emerald-50 border border-emerald-200' : 'bg-indigo-50 border-2 border-indigo-300'}`}>
            <Label className="text-sm font-medium mb-3 block text-indigo-900">
              Who are your target customers? <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => selectRevenueTargetType('smb')}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  revenueTargetType === 'smb' 
                    ? 'border-emerald-500 bg-white shadow-md ring-2 ring-emerald-200' 
                    : 'border-slate-200 bg-white hover:border-indigo-300'
                }`}
                data-testid="target-smb"
              >
                <div className="font-semibold text-slate-900">SMBs</div>
                <p className="text-xs text-slate-500 mt-1">Small to mid-size businesses</p>
                <p className="text-xs text-indigo-600 mt-2">$0 - $50M ranges</p>
              </button>
              <button
                onClick={() => selectRevenueTargetType('enterprise')}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  revenueTargetType === 'enterprise' 
                    ? 'border-emerald-500 bg-white shadow-md ring-2 ring-emerald-200' 
                    : 'border-slate-200 bg-white hover:border-indigo-300'
                }`}
                data-testid="target-enterprise"
              >
                <div className="font-semibold text-slate-900">Enterprise</div>
                <p className="text-xs text-slate-500 mt-1">Large corporations</p>
                <p className="text-xs text-indigo-600 mt-2">$50M - $1B+ ranges</p>
              </button>
            </div>
          </div>

          {/* Currency Fallback Selection */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="text-sm font-medium mb-2 block">Default Currency</Label>
            <p className="text-xs text-slate-500 mb-3">Used when currency is not specified in the data</p>
            <Select value={revenueCurrencyFallback} onValueChange={setRevenueCurrencyFallback}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">$ USD - US Dollar</SelectItem>
                <SelectItem value="EUR">€ EUR - Euro</SelectItem>
                <SelectItem value="GBP">£ GBP - British Pound</SelectItem>
                <SelectItem value="CAD">$ CAD - Canadian Dollar</SelectItem>
                <SelectItem value="AUD">$ AUD - Australian Dollar</SelectItem>
                <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                <SelectItem value="JPY">¥ JPY - Japanese Yen</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Selected Ranges */}
          {selectedRevenueRanges.length > 0 && (
            <div className={`p-4 rounded-lg ${selectedRevenueRanges.length >= 2 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              <Label className={`text-sm font-medium mb-2 block ${selectedRevenueRanges.length >= 2 ? 'text-emerald-900' : 'text-amber-900'}`}>
                Selected Ranges ({selectedRevenueRanges.length}/2 minimum)
                {selectedRevenueRanges.length < 2 && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedRevenueRanges.map((range, i) => (
                  <span key={i} className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${selectedRevenueRanges.length >= 2 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {range}
                    <button 
                      onClick={() => removeRange(range)}
                      className="hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              {selectedRevenueRanges.length < 2 && (
                <p className="text-xs text-amber-700 mt-2">Please select at least {2 - selectedRevenueRanges.length} more range(s)</p>
              )}
            </div>
          )}

          {revenueSuggestions ? (
            <>
              {/* Data Analysis */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">Data Analysis</Label>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Min</p>
                    <p className="font-medium">{revenueSuggestions.analysis?.min || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Max</p>
                    <p className="font-medium">{revenueSuggestions.analysis?.max || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Average</p>
                    <p className="font-medium">{revenueSuggestions.analysis?.average || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Values</p>
                    <p className="font-medium">{revenueSuggestions.analysis?.count || 0}</p>
                  </div>
                </div>
              </div>
              
              {/* Add from Suggested Ranges */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Add from suggestions (click to add)</Label>
                <div className="flex flex-wrap gap-2">
                  {revenueSuggestions.suggested_ranges?.filter(r => !selectedRevenueRanges.includes(r)).map((range, i) => (
                    <button
                      key={i}
                      onClick={() => addSuggestedRange(range)}
                      className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm hover:bg-indigo-100 hover:text-indigo-700 transition-colors"
                    >
                      + {range}
                    </button>
                  ))}
                </div>
                {(!revenueSuggestions.suggested_ranges || revenueSuggestions.suggested_ranges.filter(r => !selectedRevenueRanges.includes(r)).length === 0) && (
                  <p className="text-xs text-slate-500 italic">All suggested ranges have been added</p>
                )}
              </div>

              {/* Custom Range Input */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Add custom range</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., $25M - $75M"
                    value={customRangeInput}
                    onChange={(e) => setCustomRangeInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomRange()}
                    className="flex-1"
                    data-testid="custom-range-input"
                  />
                  <Button onClick={addCustomRange} variant="outline" data-testid="add-custom-range">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onSave} 
            data-testid="save-revenue-btn"
            disabled={!isValid}
            className={!isValid ? 'opacity-50 cursor-not-allowed' : ''}
          >
            Apply Ranges
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RevenueRangesDialog;
